/**
 * The icon set. Inline SVG on a 24px grid, `currentColor`, and no emoji anywhere in the
 * interface.
 *
 * Terminals are butt caps and joins are mitred, which is the same decision as the zero radius
 * on cards and photos: the drawing has corners because the brand has corners. A rounded stroke
 * set would read as a different product bolted onto this one.
 */

export type IconName =
  | 'menu'
  | 'search'
  | 'close'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'arrow-left'
  | 'phone'
  | 'mail'
  | 'message'
  | 'shuffle'
  | 'eye'
  | 'eye-off'
  | 'bolt'
  | 'moon'
  | 'pause'
  | 'incall'
  | 'outcall'
  | 'chevron-up'
  | 'bookmark'
  | 'external'
  | 'random-gallery'
  | 'scroll-all'
  | 'towel-map'
  | 'arrow-up-right';

const PATHS: Record<IconName, string> = {
  menu: 'M3 6h18M3 12h18M3 18h18',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14ZM16.2 16.2 21 21',
  close: 'M5 5l14 14M19 5 5 19',
  'chevron-down': 'm5 9 7 7 7-7',
  'chevron-up': 'm5 15 7-7 7 7',
  'chevron-left': 'm15 5-7 7 7 7',
  'chevron-right': 'm9 5 7 7-7 7',
  'arrow-left': 'M20 12H4M10 6l-6 6 6 6',
  // A link that leaves the site: a box with an arrow out of its corner.
  external: 'M18 13v8H3V6h8M14 3h7v7M21 3 10 14',
  phone: 'M4 4h5l2 5-3 2a12 12 0 0 0 5 5l2-3 5 2v5h-2A14 14 0 0 1 4 6Z',
  mail: 'M3 5h18v14H3zM3 5l9 7 9-7',
  message: 'M3 4h18v12H10l-7 5V4Z',
  shuffle: 'M3 6h4l10 12h4M3 18h4l3-3.6M14.4 8.6 17 6h4M18 3l3 3-3 3M18 15l3 3-3 3',
  eye: 'M2 12c2-4.4 5.6-7 10-7s8 2.6 10 7c-2 4.4-5.6 7-10 7S4 16.4 2 12ZM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z',
  'eye-off':
    'M2 12c2-4.4 5.6-7 10-7s8 2.6 10 7c-2 4.4-5.6 7-10 7S4 16.4 2 12ZM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM4 3l16 18',
  bolt: 'M13 2 4.5 13.5H11L9.5 22 19 10.5h-6.5L13 2Z',
  moon: 'M20 14.5A8.5 8.5 0 1 1 9.5 4 7 7 0 0 0 20 14.5Z',
  pause: 'M8 5v14M16 5v14',
  // Saved to a list: a squared ribbon, cut at the foot, in the set's mitred geometry.
  bookmark: 'M6 3h12v18l-6-5-6 5Z',
  // The homepage's four ways in. The random gallery is a 3:4 photograph on a pile, with a die's
  // three pips in it: a picture, chosen by chance.
  'random-gallery': 'M8 7V3h13v16h-4M3 7h14v14H3zM6.5 10.5h1v1h-1zM9.5 13.5h1v1h-1zM12.5 16.5h1v1h-1z',
  // Everybody in one run: the phone card, a photograph beside its facts, stacked three high and
  // running off the foot, with the arrow of a list that keeps going.
  'scroll-all': 'M3 3h4v5H3zM3 10h4v5H3zM3 17h4v4M10 5h5M10 12h5M10 19h5M20 3v17M16.5 16.5 20 20l3.5-3.5',
  // The venues: a folded map with one squared mark in its middle panel.
  'towel-map': 'M2 6l6.5-3 7 3L22 3v15l-6.5 3-7-3L2 21zM8.5 3v15M15.5 6v15M12 10.5l1.25 1.5L12 13.5 10.75 12z',
  // The badge on an action that leaves the site.
  'arrow-up-right': 'M7 17 17 7M9 7h8v8',
  // The door is the premises and the arrow is who travels. In-call is an arrow crossing into a
  // door on the right: the visitor goes to them. Out-call is an arrow leaving a door on the
  // left: they travel to the visitor.
  //
  // The door is a **closed** rectangle with a handle, not three sides of one. Drawn open it
  // reads as a bracket at 20px, which is where the pair stopped meaning anything: two brackets
  // and two arrows are the same drawing twice.
  incall: 'M12 3h9v18h-9zM14.5 12h1.5M2 12h7.5M6 8.5 9.5 12 6 15.5',
  outcall: 'M12 3H3v18h9zM9.5 12H8M14.5 12H22M18.5 8.5 22 12l-3.5 3.5',
};

export default function Icon({
  name,
  size = 24,
  className,
}: {
  name: IconName;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="butt"
      strokeLinejoin="miter"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
